package ai.jessie.web.profile

import ai.jessie.shared.profile.EmailChangeNavigationResponse
import ai.jessie.shared.profile.OwnProfileResponse
import ai.jessie.shared.profile.ProfileErrorResponse
import ai.jessie.shared.profile.ProfileProposalRequest
import ai.jessie.shared.profile.ProfileReviewResponse
import ai.jessie.shared.profile.SaveProfileRequest
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

// UC-026. The session cookie alone identifies the account; no account or member ID is ever sent.
interface ProfilePort {
    suspend fun read(organisationId: String?): OwnProfileResponse

    suspend fun review(proposal: ProfileProposalRequest): ProfileReviewResponse

    suspend fun confirm(proposal: ProfileProposalRequest): OwnProfileResponse
}

// A3: separate from profile text and available before onboarding (approved B6).
interface EmailChangePort {
    suspend fun start(): EmailChangeNavigationResponse

    suspend fun check(): EmailChangeNavigationResponse

    suspend fun cancel()
}

val LocalProfilePort =
    staticCompositionLocalOf<ProfilePort> {
        error("Profile port must be supplied by the application")
    }

val LocalEmailChangePort =
    staticCompositionLocalOf<EmailChangePort> {
        error("Email change port must be supplied by the application")
    }

class ProfileRequestFailure(val status: Int, val detail: ProfileErrorResponse) :
    Exception("Profile request failed")

class BrowserProfilePort : ProfilePort, EmailChangePort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun read(organisationId: String?): OwnProfileResponse {
        require(organisationId == null || validOrganisationId(organisationId))
        val query = organisationId?.let { "?organisationId=$it" }.orEmpty()
        return json.decodeFromString(request("/profile$query"))
    }

    override suspend fun review(proposal: ProfileProposalRequest) =
        json.decodeFromString<ProfileReviewResponse>(
            request("/profile/review", json.encodeToString(proposal))
        )

    override suspend fun confirm(proposal: ProfileProposalRequest) =
        json.decodeFromString<OwnProfileResponse>(
            request(
                "/profile/confirm",
                json.encodeToString(SaveProfileRequest(proposal, confirmed = true)),
            )
        )

    override suspend fun start() =
        json.decodeFromString<EmailChangeNavigationResponse>(request("/profile/email/start", ""))

    override suspend fun check() =
        json.decodeFromString<EmailChangeNavigationResponse>(request("/profile/email/check", ""))

    override suspend fun cancel() {
        try {
            request("/profile/email/cancel", "")
        } catch (failure: ProfileRequestFailure) {
            // The shared transport accepts only 200; cancellation succeeds without content.
            if (failure.status != HTTP_NO_CONTENT) throw failure
        }
    }

    private suspend fun request(path: String, body: String? = null) =
        apiRequest(json, path, body) { status, text ->
            val detail = json.decodeProblem(text, ProfileErrorResponse(""))
            ProfileRequestFailure(status, detail)
        }
}

private const val HTTP_NO_CONTENT = 204
